const kkCollectionUrl = new URL(window.location.href);
if (kkCollectionUrl.searchParams.has('page') && !kkCollectionUrl.searchParams.has('kk_page')) {
  const legacyPage = kkCollectionUrl.searchParams.get('page');
  kkCollectionUrl.searchParams.delete('page');
  if (legacyPage && legacyPage !== '1') kkCollectionUrl.searchParams.set('kk_page', legacyPage);
  window.location.replace(kkCollectionUrl.href);
}

class KKCollectionPager extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-kk-collection-product]'));
    this.controls = this.querySelector('[data-kk-collection-pagination]');
    this.pageSize = Math.max(1, Number.parseInt(this.dataset.pageSize || '1', 10));
    this.mode = this.dataset.paginationMode || 'paginated';
    this.pageCount = Math.max(1, Math.ceil(this.items.length / this.pageSize));
    this.visiblePages = 1;
    this.render();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  render() {
    if (!this.controls) return;
    this.controls.replaceChildren();
    this.observer?.disconnect();

    if (this.mode === 'paginated') {
      this.renderPaginated();
    } else {
      this.renderProgressive();
    }
  }

  requestedPage() {
    const value = Number.parseInt(new URL(window.location.href).searchParams.get('kk_page') || '1', 10);
    return Math.min(this.pageCount, Math.max(1, Number.isFinite(value) ? value : 1));
  }

  renderPaginated() {
    const currentPage = this.requestedPage();
    const start = (currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.items.forEach((item, index) => {
      item.hidden = index < start || index >= end;
    });

    this.updateStructuredData();
    if (this.pageCount <= 1) return;
    const wrapper = document.createElement('div');
    wrapper.className = 'pagination-wrapper';
    const nav = document.createElement('nav');
    nav.className = 'pagination';
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', this.dataset.label || 'Pagination');
    const list = document.createElement('ul');
    list.className = 'pagination__list list-unstyled';
    list.setAttribute('role', 'list');

    if (currentPage > 1) {
      list.append(this.controlItem(this.pageLink(currentPage - 1, this.dataset.previousLabel || 'Previous', 'pagination__item pagination__item--next motion-reduce', this.dataset.previousLabel || 'Previous')));
    }

    for (const part of this.pageParts(currentPage)) {
      const item = document.createElement('li');
      if (part === 'gap') {
        const gap = document.createElement('span');
        gap.className = 'pagination__item';
        gap.textContent = '…';
        item.append(gap);
      } else if (part === currentPage) {
        const current = document.createElement('span');
        current.className = 'pagination__item pagination__item--current';
        current.setAttribute('aria-current', 'page');
        current.setAttribute('aria-label', this.pageLabel(part));
        current.textContent = String(part);
        item.append(current);
      } else {
        item.append(this.pageLink(part, String(part), 'pagination__item', this.pageLabel(part)));
      }
      list.append(item);
    }

    if (currentPage < this.pageCount) {
      list.append(this.controlItem(this.pageLink(currentPage + 1, this.dataset.nextLabel || 'Next', 'pagination__item pagination__item--prev motion-reduce', this.dataset.nextLabel || 'Next')));
    }

    nav.append(list);
    wrapper.append(nav);
    this.controls.append(wrapper);
  }

  renderProgressive() {
    this.visiblePages = Math.max(1, this.requestedPage());
    this.updateProgressiveItems();
    if (this.visiblePages >= this.pageCount) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'pagination-wrapper';
    const center = document.createElement('div');
    center.className = 'center';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'infinite-scroll button button--tertiary';
    button.textContent = this.dataset.showMoreLabel || 'Show more';
    button.addEventListener('click', () => {
      this.visiblePages += 1;
      this.updateProgressiveItems();
      if (this.visiblePages >= this.pageCount) wrapper.remove();
    });
    center.append(button);
    wrapper.append(center);
    this.controls.append(wrapper);

    if (this.mode === 'infinite' && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) button.click();
      }, { rootMargin: '300px 0px' });
      this.observer.observe(button);
    }
  }

  updateProgressiveItems() {
    const end = this.visiblePages * this.pageSize;
    this.items.forEach((item, index) => {
      item.hidden = index >= end;
    });
    this.updateStructuredData();
  }

  updateStructuredData() {
    const script = this.querySelector('[data-kk-collection-schema]');
    if (!script) return;
    try {
      const schema = JSON.parse(script.textContent);
      schema.itemListElement = this.items.flatMap((item, index) => item.hidden ? [] : [{
        '@type': 'ListItem',
        position: index + 1,
        name: item.dataset.kkSchemaName,
        url: item.dataset.kkSchemaUrl
      }]);
      script.textContent = JSON.stringify(schema).replace(/</g, '\u003c');
    } catch (error) {
      // Invalid optional metadata must never interrupt collection navigation.
      console.warn('Collection structured data could not be updated.', error);
    }
  }

  pageParts(currentPage) {
    if (this.pageCount <= 7) return Array.from({ length: this.pageCount }, (_, index) => index + 1);
    const pages = new Set([1, this.pageCount]);
    for (let page = currentPage - 2; page <= currentPage + 2; page += 1) {
      if (page > 1 && page < this.pageCount) pages.add(page);
    }
    if (currentPage <= 3) [2, 3, 4].forEach((page) => pages.add(page));
    if (currentPage >= this.pageCount - 2) [this.pageCount - 3, this.pageCount - 2, this.pageCount - 1].forEach((page) => pages.add(page));
    const sorted = Array.from(pages).filter((page) => page > 0 && page <= this.pageCount).sort((a, b) => a - b);
    const parts = [];
    sorted.forEach((page, index) => {
      if (index > 0 && page - sorted[index - 1] > 1) parts.push('gap');
      parts.push(page);
    });
    return parts;
  }

  pageLink(page, text, className, ariaLabel) {
    const link = document.createElement('a');
    link.href = this.pageUrl(page);
    link.className = className;
    link.setAttribute('aria-label', ariaLabel);
    link.textContent = text;
    return link;
  }

  controlItem(control) {
    const item = document.createElement('li');
    item.append(control);
    return item;
  }

  pageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.delete('page');
    if (page === 1) url.searchParams.delete('kk_page');
    else url.searchParams.set('kk_page', String(page));
    url.hash = 'product-grid';
    return url.href;
  }

  pageLabel(page) {
    return (this.dataset.pageLabel || 'Page [page]').replace('[page]', String(page));
  }
}

if (!customElements.get('kk-collection-pager')) {
  customElements.define('kk-collection-pager', KKCollectionPager);
}
